import type { NotificationSettings } from '@sirocco/shared';
import { users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { SettingGroup, SettingRow } from '../../components/ui/Bits';
import { Toggle } from '../../components/ui/Field';
import { useAsync } from '../../hooks/useAsync';
import { t } from '../../i18n';

/**
 * Un interrupteur, son titre.
 *
 * Sans phrase d'explication : « Messages » n'a pas besoin qu'on precise
 * qu'il s'agit d'un message, et huit lignes de commentaire faisaient un
 * paragraphe la ou l'on vient juste couper un interrupteur.
 */
const CHOICES: { key: keyof NotificationSettings; label: string }[] = [
  { key: 'messages', label: t.profil.notifications.choix.messages },
  { key: 'calls', label: t.profil.notifications.choix.appels },
  { key: 'friends', label: t.profil.notifications.choix.amis },
  { key: 'friendPosts', label: t.profil.notifications.choix.postsAmis },
  { key: 'events', label: t.profil.notifications.choix.evenements },
  { key: 'eventAccepted', label: t.profil.notifications.choix.acceptee },
  /*
    Distinct de la ligne au-dessus : celle-la previent de ce qui arrive a
    une sortie ou l'on va, celle-ci de ce qui arrive a une sortie qu'on
    organise. Le libelle dit « mes » parce que c'est tout ce qui les
    separe.
  */
  { key: 'eventRequests', label: t.profil.notifications.choix.inscriptions },
  { key: 'eventLeaves', label: t.profil.notifications.choix.desinscriptions },
  { key: 'activity', label: t.profil.notifications.choix.reactions },
  { key: 'mentions', label: t.profil.notifications.choix.mentions },
];

/**
 * Ce dont on accepte d'etre prevenu.
 *
 * Un interrupteur par nature d'evenement : on peut vouloir savoir qu'un
 * ami ecrit sans vouloir savoir qu'un inconnu a aime un post. Tout est
 * actif au depart — c'est en decouvrant ce qui derange qu'on vient
 * couper, et une notification qu'on n'a jamais recue ne se demande pas.
 *
 * Chaque reglage est applique a l'ecran avant l'aller-retour reseau : on
 * ne recharge pas la page entiere pour un interrupteur. Si l'ecriture
 * echoue, `reload()` remet l'affichage en accord avec le serveur.
 */
export function NotificationsScreen() {
  const { data, loading, error, reload, setData } = useAsync(
    () => usersApi.settings(),
    [],
    'settings',
  );

  function patch(next: Partial<NotificationSettings>) {
    setData((previous) => ({
      ...previous,
      notifications: { ...previous.notifications, ...next },
    }));
    usersApi.updateNotifications(next).catch(reload);
  }

  const notifications = data?.notifications;

  return (
    <SubScreen title={t.profil.notifications.titre}>
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {/*
        Ni phrase d'accueil ni titre de groupe : le titre de l'ecran dit
        deja de quoi il s'agit, et le libelle d'une ligne dit le reste. Un
        bandeau de plus n'aurait fait que retarder les interrupteurs.
      */}
      {notifications && (
        <SettingGroup>
          {CHOICES.map((choice) => (
            <SettingRow
              key={choice.key}
              label={choice.label}
              trailing={
                <Toggle
                  checked={notifications[choice.key]}
                  onChange={(next) => patch({ [choice.key]: next })}
                  label={choice.label}
                />
              }
            />
          ))}
        </SettingGroup>
      )}
    </SubScreen>
  );
}
